/**
 * Avatar circular. Si hay url, muestra imagen. Sino iniciales sobre fondo
 * con color determinístico (hash del seed → paleta de 10 colores).
 *
 * Para URLs propias de la app (/api/...) hacemos fetch autenticado con el
 * access_token y convertimos a Blob URL, porque <img src=> no manda
 * Authorization. URLs externas (http/https) van directo al <img>.
 *
 * Tamaños: sm 28px, md 40px, lg 56px, xl 80px.
 */
import { useEffect, useState } from "react";
import { cn } from "@/lib/cn";
import { useAuthStore } from "@/stores/auth";

type Size = "sm" | "md" | "lg" | "xl";

type Props = {
  url?: string | null;
  name: string;
  /** Seed para el color. Por default usa `name`. Pasá user_id para que el color sea
   *  estable aún si el user cambia de nombre. */
  seed?: string;
  size?: Size;
  className?: string;
  /** Si true, muestra un punto verde de "activo" en la esquina inferior derecha. */
  showOnline?: boolean;
};

const SIZES: Record<Size, { box: string; text: string; dot: string }> = {
  sm: { box: "size-7 text-[10px]", text: "font-semibold", dot: "size-2" },
  md: { box: "size-10 text-sm", text: "font-semibold", dot: "size-2.5" },
  lg: { box: "size-14 text-lg", text: "font-bold", dot: "size-3" },
  xl: { box: "size-20 text-2xl", text: "font-bold", dot: "size-3.5" },
};

// Paleta hex con buen contraste sobre texto blanco.
const PALETTE = [
  "#1F8A55", // verde brand
  "#0F766E", // teal
  "#2563EB", // azul
  "#7C3AED", // violeta
  "#DB2777", // rosa
  "#DC2626", // rojo
  "#EA580C", // naranja
  "#CA8A04", // amarillo oscuro
  "#475569", // slate
  "#0891B2", // cyan
];

function hashString(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h + s.charCodeAt(i)) & 0xffffffff;
  }
  return Math.abs(h);
}

function colorFor(seed: string): string {
  return PALETTE[hashString(seed) % PALETTE.length];
}

function initialsOf(name: string): string {
  const cleaned = name.replace(/\(.*?\)/g, "").trim();
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function useAuthenticatedImage(url: string | null | undefined): string | null {
  const accessToken = useAuthStore((s) => s.accessToken);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!url || !url.startsWith("/api/")) {
      setBlobUrl(null);
      return;
    }
    if (!accessToken) return;
    let cancelled = false;
    let createdBlob: string | null = null;
    (async () => {
      try {
        const res = await fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } });
        if (!res.ok) {
          if (!cancelled) setBlobUrl(null);
          return;
        }
        const blob = await res.blob();
        if (cancelled) return;
        createdBlob = URL.createObjectURL(blob);
        setBlobUrl(createdBlob);
      } catch {
        if (!cancelled) setBlobUrl(null);
      }
    })();
    return () => {
      cancelled = true;
      if (createdBlob) URL.revokeObjectURL(createdBlob);
    };
  }, [url, accessToken]);

  return blobUrl;
}

export function Avatar({
  url,
  name,
  seed,
  size = "md",
  className,
  showOnline = false,
}: Props) {
  const sz = SIZES[size];
  const color = colorFor(seed || name);
  const initials = initialsOf(name);
  const isInternal = !!url && url.startsWith("/api/");
  const authenticated = useAuthenticatedImage(isInternal ? url : null);
  const resolved = isInternal ? authenticated : url ?? null;

  return (
    <div className={cn("relative inline-flex shrink-0", className)}>
      {resolved ? (
        <img
          src={resolved}
          alt={name}
          className={cn(sz.box, "rounded-full object-cover ring-1 ring-slate-200")}
        />
      ) : (
        <div
          className={cn(
            sz.box,
            "flex items-center justify-center rounded-full text-white",
            sz.text,
          )}
          style={{ backgroundColor: color }}
          aria-label={name}
        >
          {initials}
        </div>
      )}
      {showOnline && (
        <span
          className={cn(
            sz.dot,
            "absolute right-0 bottom-0 rounded-full bg-emerald-500 ring-2 ring-white",
          )}
        />
      )}
    </div>
  );
}
